<template>
  <base-modal
    :ref="modalKey"
    :show="open"
    @on-cancel="hide()"
    :showConfirmButton="false"
    :closeText="$t('Close')"
    max-width="80%"
  >
   <v-row dense class="align-center">
        <v-col cols="6" class="d-flex">
            <span class="font-weight-bold pl-2">{{ modalTitle }}</span>
        </v-col>

        <v-col cols="3" class="d-flex justify-end">
            <v-btn small color="primary" @click="openAddModal">
            {{ $t("Add") }}
            <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-col>

        <v-col cols="3" class="d-flex justify-end">
            <v-text-field
            dense
            v-model="filter"
            :label="$t('Search') + '...'"
            prepend-icon="mdi-magnify"
            @input="changeFilter"
            class="mt-1"
            />
        </v-col>
    </v-row>

    <v-row justify="center" align="center" v-if="loading">
      <div class="text-center">
        <div>
          <v-progress-circular
            class="my-4"
            :size="50"
            width="5"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
        <p class="display-1">{{ $t("Loading") }}...</p>
      </div>
    </v-row>

    <v-simple-table dense v-if="!loading" class="internal-table-wrapper">
      <thead>
        <tr>
          <th
            v-for="(column, index) in internalTable.columns"
            :key="index"
            @click="onTableHeadClicked(column)"
            class="text-left"
          >
            <h4 href="javascript:void(0)" style="cursor: pointer">
              {{ getColumnLabel(column) }}
              <span
                v-if="
                  column.name === paginationSetting.orderBy &&
                  !paginationSetting.desc
                "
                >↓</span
              >
              <span
                v-if="
                  column.name === paginationSetting.orderBy &&
                  paginationSetting.desc
                "
                >↑</span
              >
            </h4>
          </th>
          <th>
            <h4 align="right">{{ $t("Actions") }}</h4>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="dataSource.length === 0">
          <td colspan="9999" class="text-center table-no-data">
            {{ this.$t("NoData") }}
          </td>
        </tr>
        <tr v-for="(row, index) in dataSource" :key="index">
          <td v-for="(item, columnIndex) in row.Items" :key="columnIndex">
            {{ item }}
          </td>
          <td class="text-right">
            <v-btn icon small dark color="primary" @click="openEditModal(row)">
              <v-icon small dark>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small dark color="red" @click="removeItem(row)">
              <v-icon small dark>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <PaginationComponent
      :ref="paginationComponentKey"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @on-page-change="getData"
    >
    </PaginationComponent>

    <RowDataModal
      :ref="rowDataModalKey"
      @item-add="addItem"
      @item-update="updateItem"
    />
  </base-modal>
</template>

<script>
import InternalTable from '@/ecm/models/internalTable/InternalTable';
import InternalTableRow from '@/ecm/models/internalTable/InternalTableRow';
import InternalTableRowItem from '@/ecm/models/internalTable/InternalTableRowItem';
import PaginationSetting from '@/ecm/models/PaginationSetting.js';
import InternalTableService from '@/services/InternalTableService';
import RowDataModal from '@/components/administration/internalTable/RowDataModal.vue';
import PaginationComponent from '../../base/PaginationComponent.vue';

export default {
    name: 'InternalTableDataModal',
    components: {
        PaginationComponent,
        RowDataModal,
    },
    data() {
        return {
            modalKey: 'internalTableDataModal',
            rowDataModalKey: 'rowDataModal',
            dataSource: [],
            paginationSetting: new PaginationSetting(),
            totalPages: 1,
            paginationComponentKey: 'paginationComponentKey',
            open: false,
            relatedTableListElements: [],
            internalTable: new InternalTable(),
            filter: '',
            loading: false,
            totalItems: 1,
        };
    },
    computed: {
        modalTitle() {
            return (
                this.$t('TableData') +
                (this.internalTable.name ? ' - ' + this.internalTable.name : '')
            );
        },
        modalInstance() {
            return this.$refs[this.modalKey];
        },
        rowDataModaInstance() {
            return this.$refs[this.rowDataModalKey];
        },
        paginationComponentInstance() {
            return this.$refs[this.paginationComponentKey];
        },
    },
    methods: {
        show() {
            this.open = true;
            this.$nextTick(() => {
                this.paginationComponentInstance.reset(true);
            });
        },
        hide() {
            this.open = false;
        },
        async getLinkedTableData(relatedTableId) {
            await InternalTableService.getLinkedTableData(parseInt(relatedTableId)).then(
                (data) => {
                    var relatedTableList = [];
                    for (let i = 0; i < data.length; i++) {
                        const relatedTableItem = {
                            id: i + 1,
                            text: data[i].text,
                            selected: data[i].selected
                        };
                        relatedTableList.push(relatedTableItem);
                    }
                    this.relatedTableListElements[relatedTableId] = relatedTableList;
                }
            );
        },
        getColumnLabel(column) {
            return column.isRequired ? column.name + '*' : column.name;
        },
        getRowWithoutId(row) {
            return Object.keys(row)
                .filter((key) => key != 'Id')
                .reduce((obj, key) => {
                    obj[key] = row[key];
                    return obj;
                }, {});
        },
        openAddModal() {
            const columns = [];
            for (const column of this.internalTable.columns) {
                columns.push({
                    name: column.name,
                    isRequired: column.isRequired,
                    isUnique: column.isUnique,
                    relatedTableData: this.relatedTableListElements[parseInt(column.relatedTableId)] || [],
                    isRelatedTable: !!column.relatedTableId,
                    value: '',
                });
            }
            this.rowDataModaInstance.open(0, columns);
        },
        addItem(item) {
            var newRow = new InternalTableRow();
            newRow.id = 0;
            for (const column of item) {
                var rowItem = new InternalTableRowItem();
                rowItem.column.name = column.name;
                rowItem.value = column.value;
                rowItem.column.isUnique = column.isUnique;
                newRow.items.push(rowItem);
            }
            InternalTableService.saveDataRow(this.internalTable.id, newRow)
                .then((result) => {
                    if (result) {
                        this.getData();
                        this.rowDataModaInstance.hide();
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        openEditModal(item) {
            const columns = [];
            for (const column of this.internalTable.columns) {
                columns.push({
                    name: column.name,
                    isRequired: column.isRequired,
                    value: item.Items[column.name],
                    isUnique: column.isUnique,
                    relatedTableData: this.relatedTableListElements[parseInt(column.relatedTableId)] || [],
                    isRelatedTable: !!column.relatedTableId,
                });
            }
            this.rowDataModaInstance.open(item.Id, columns);
        },
        updateItem(id, item) {
            var newRow = new InternalTableRow();
            newRow.id = id;
            for (const column of item) {
                var rowItem = new InternalTableRowItem();
                rowItem.column.name = column.name;
                rowItem.value = column.value;
                rowItem.column.isUnique = column.isUnique;
                newRow.items.push(rowItem);
            }
            InternalTableService.saveDataRow(this.internalTable.id, newRow)
                .then((result) => {
                    if (result) {
                        this.getData();
                        this.rowDataModaInstance.hide();
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        removeItem(item) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    InternalTableService.deleteDataRow(this.internalTable.id, item.Id)
                        .then((result) => {
                            if (result) {
                                this.getData();
                                this.rowDataModaInstance.hide();
                            }
                        })
                        .catch(() => {
                            // error
                        })
                        .then(() => {
                            //always
                            this.modalInstance.progress(false);
                        });
                }
            });
        },
        onTableHeadClicked(column) {
            if (
                this.paginationSetting.orderBy != column.name ||
                this.paginationSetting.orderBy == ''
            ) {
                this.paginationSetting.desc = false;
            } else {
                this.paginationSetting.desc =
                    this.paginationSetting.desc === false;
            }
            this.paginationSetting.orderBy = column.name;
            this.getData();
        },
        async edit(dto) {
            await InternalTableService.getColumns(dto.Id).then((data) => {
                dto.Columns = data;
            });
            this.internalTable = new InternalTable(dto);
            this.dataSource = [];
            this.rowsToSave = [];

            await Promise.all(
                this.internalTable.columns
                    .filter((c) => c.relatedTableId)
                    .map(async (column) => {
                        await this.getLinkedTableData(column.relatedTableId);
                    })
            );
            this.show();
        },
        getData(paginationOptions) {
            this.loading = true;
            this.paginationSetting.filter = this.filter;

            if (paginationOptions && paginationOptions.page)
                this.paginationSetting.page = paginationOptions.page;

            if (paginationOptions && paginationOptions.pageSize)
                this.paginationSetting.pageSize = paginationOptions.pageSize;

            InternalTableService.getPaginatedData(
                this.internalTable.id,
                this.paginationSetting
            )
                .then((data) => {
                    this.totalPages = Math.ceil(
                        data.Count / this.paginationSetting.pageSize
                    );
                    if (data.Result.length === 0 && this.paginationSetting.page > 1) {
                        // If it's empty, go back to the previous page
                        this.$refs.paginationComponentKey.changePage(this.paginationSetting.page - 1);
                    }
                    if (this.totalPages === 0) this.totalPages = 1;
                    this.totalItems = data.Count;
                    this.dataSource = data.Result.map((row) => {
                        var gridRow = {
                            Id: row.Id,
                            Items: {},
                        };
                        row.Items.forEach((item) => {
                            gridRow.Items[item.Column.Name] = item.Value;
                        });
                        return gridRow;
                    });
                    this.loading = false;
                })
                .catch((error) => {
                    if (
                        error.response.data &&
                        error.response.status === 404) {
                        this.hide();
                        this.$emit('on-table-notFound', this.internalTable);
                    }
                });
        },
        changeFilter() {
            this.paginationComponentInstance.reset();
        },
    },
};
</script>

<style lang="scss">
.internal-table-wrapper {
    table {
        font-family: arial, sans-serif !important;
        font-size: 13px !important;
        border: 1px solid #cdcdcd !important;
        border-collapse: collapse !important;
        width: 100% !important;
    }
    td,
    th {
        text-align: left !important;
        padding: 8px !important;
    }
    th:hover {
        background-color: #efeeee !important;
        cursor: pointer !important;
    }

    tr:nth-child(even) {
        background-color: #dddddd !important;
    }
}
</style>
